/* eslint-disable react/prop-types */

import React, { useEffect, useState } from 'react';
import { Card, CardFooter, CardBody, Alert, Row, Col } from 'reactstrap';
import { withFormik } from 'formik';
import * as Yup from 'yup';
import { Link, withRouter } from 'react-router-dom';
import {
  Button,
  Field,
  FormAlerts,
  useFeatures,
  CardTitle,
  useSession,
} from '@cordata/buffet';

import { adminInstance as axios, toastSuccess } from '~/utils';

const formEnhancer = withFormik({
  validationSchema: Yup.object().shape({
    departmentLabel: Yup.string().required(),
    organization: Yup.mixed().required(),
    division: Yup.mixed().required(),
  }),

  mapPropsToValues: props => ({
    id: props.data && props.data.id,
    ...(props.data && props.data.attributes),
  }),

  handleSubmit: (values, { setErrors, setSubmitting, ...bag }) => {
    const { props: { history } } = bag;

    const p = {
      ...values,
      departmentName: values.departmentLabel,
      orgUid: values.organization.id,
      divisionUid: values.division.id,
    };

    delete p.organization;
    delete p.division;

    const method = values.id ? 'put' : 'post';
    const url = `/department${values.id ? `/${values.id}` : ''}`;

    axios[method](url, p)
      .then(() => toastSuccess())
      .then(() => history.push('/admin/departments'))
      .catch((e) => {
        setErrors({
          server: e.response.data,
          orgUid: e.response.data.message.includes('org_uid'),
          divisionUid: e.response.data.message.includes('division_uid'),
          ...e.response.data.errors,
        });
      })
      .then(() => setSubmitting(false));
  },

  displayName: 'DepartmentsFormik',
  validateOnChange: false,
  validateOnBlur: false,
});

const DepartmentsForm = ({
  values,
  errors,
  handleSubmit,
  isSubmitting,
  submitCount,
  setFieldValue,
}) => {
  const { current } = useSession();
  const { hasAppKickstarter } = useFeatures();
  const [organizations, setOrganizations] = useState([]);
  const [divisions, setDivisions] = useState([]);

  useEffect(() => {
    if (hasAppKickstarter) {
      axios
        .get('organization?page=1&page-size=10000&sort=org-name')
        .then(({ data: { data: r } }) =>
          setOrganizations(r));
    }
  }, [hasAppKickstarter]);

  useEffect(() => {
    if (hasAppKickstarter) {
      if (values.organization) {
        const orgUid = values.organization.id;
        axios
          .get('division', {
            params: {
              'org-uid': orgUid,
              sort: 'division-name',
            },
          })
          .then(({ data: { data: r } }) =>
            setDivisions(r));
      }
    } else if (current) {
      setDivisions(current.attributes.accessibleDivisions
        .map(div => ({ ...div, id: div.divisionUid, attributes: div })));
    }
  }, [current, values.organization]);

  useEffect(() => {
    const orgUid = localStorage.getItem('org') || values.orgUid;
    if (orgUid) {
      axios
        .get(`organization/${orgUid}`)
        .then(({ data: { data: r } }) =>
          setFieldValue('organization', r));
    } else {
      setFieldValue('organization', null);
    }
  }, [
    values.orgUid,
    localStorage.getItem('org'),
  ]);

  useEffect(() => {
    const divUid = localStorage.getItem('div') || values.divisionUid;

    if (divUid) {
      axios
        .get(`division/${divUid}`)
        .then(({ data: { data: r } }) =>
          setFieldValue('division', r));
    } else {
      setFieldValue('division', null);
    }
  }, [
    values.divisionUid,
    localStorage.getItem('div'),
  ]);

  return (
    <Card>
      <form onSubmit={handleSubmit} noValidate>
        <CardBody className="p-0">
          <fieldset disabled={isSubmitting}>
            { submitCount >= 1 && errors && !errors.orgUid && <FormAlerts errors={errors} /> }
            { submitCount >= 1 && errors && (errors.orgUid || errors.divisionUid) && (
            <Alert color="danger">
              We are unable to complete this action.
              <hr />
              {errors.orgUid && 'Organization is required.'}
              {errors.divisionUid && <div>Division is required.</div>}
            </Alert>
          )}

            <Field
              name="departmentLabel"
              label="Name"
              placeholder="Enter name"
              required
            />
          </fieldset>

          <fieldset disabled={isSubmitting}>
            <CardTitle>{hasAppKickstarter && 'Organization / '}Division Information</CardTitle>

            <Row form>
              {hasAppKickstarter && (
                <Col sm>
                  <Field
                    name="organization"
                    type="select"
                    options={organizations}
                    getOptionValue={o => o.id}
                    getOptionLabel={o => o.attributes.orgName}
                    required
                  />
                </Col>
              )}

              <Col sm>
                <Field
                  name="division"
                  type="select"
                  options={divisions}
                  getOptionValue={o => o.id}
                  getOptionLabel={o => o.attributes.divisionName}
                  required
                />
              </Col>
            </Row>
          </fieldset>

          {hasAppKickstarter && (
            <fieldset disabled={isSubmitting}>
              <CardTitle>Kickstarter Information</CardTitle>

              <Field
                name="id"
                label="Department Id"
                placeholder=" "
                disabled
              />
            </fieldset>
          )}
        </CardBody>

        <CardFooter className="d-flex">
          <div className="ml-auto">
            <Button
              type="cancel"
              disabled={isSubmitting}
              tag={Link}
              to="/admin/departments"
            >
              Cancel
            </Button>

            <Button type="submit" disabled={isSubmitting}>
              Sav{isSubmitting ? 'ing ...' : 'e'}
            </Button>
          </div>
        </CardFooter>
      </form>
    </Card>
  );
};

export default withRouter(formEnhancer(DepartmentsForm));
